<template>
    <header
        ref="appHeader"
        v-qa.hydrated="'hydrated'"
        :data-qa="'app-header'"
        class="app-header"
        :class="classAppHeader"
        :style="styleAppHeader"
    >
        <div class="header-content">
            <button
                class="header-button main-menu-trigger"
                type="button"
                @click="openMenuMobile"
            >
                {{ t('header.menu') }}
            </button>

            <ButtonRound
                v-qa="'button-visit-us-mobile'"
                :icon="'location-pin'"
                :to="'/visit-us'"
                aria-label="Visit us"
                class="visit-us-mobile"
                type="link"
                size="small"
            />

            <ThLink
                v-qa="'logo-link'"
                :to="routes.home()"
                class="logo"
            >
                {{ t('globals.website_name') }}
            </ThLink>

            <HeaderMenu
                :is-menu-mobile-open
                :main-menu
                @toggle-login-form="toggleLoginForm"
                @close-menu-mobile="closeMenuMobile"
                @open-menu-mobile="openMenuMobile"
            />

            <ButtonRound
                v-if="!isLogged"
                v-qa="'button-login'"
                :icon="'profile'"
                aria-label="Account"
                class="account"
                type="link"
                @click="toggleLoginForm"
            />

            <ButtonRound
                v-else
                v-qa="'button-design-studio'"
                :icon="'profile'"
                :to="routes.designStudio()"
                aria-label="Account"
                class="account"
                type="link"
            />

            <ThLink
                class="header-button contact-us-trigger"
                type="button"
                @click="openSpeakToExpertModal"
            >
                {{ t('header.contact_us') }}
            </ThLink>

            <WishlistTrigger class="header-button saved-products-trigger" />

            <ThLink
                class="visit-us-button"
                :to="'/visit-us'"
                aria-label="Visit us"
            >
                Visit us
            </ThLink>

            <ButtonRound
                v-qa="'button-speak-to-expert-mobile'"
                :icon="'comment-rows'"
                aria-label="Speak to an expert"
                class="speak-to-expert-mobile"
                type="link"
                size="small"
                @click="openSpeakToExpertModal"
            />

            <ThLink
                v-qa="'cart'"
                :to="routes.cart()"
                class="header-button cart-trigger"
                type="button"
                rel="nofollow"
            >
                {{ t('header.cart') }}
            </ThLink>

            <div
                v-if="!hideSwitchers"
                class="switchers"
            >
                <CurrencySwitcher
                    v-qa="'currency-switcher'"
                    class="currency-switcher"
                    dropdown-direction="down"
                    is-in-footer
                />
                ∙
                <RegionSwitcher
                    v-qa="'region-switcher'"
                    class="region-switcher"
                    dropdown-direction="down"
                    is-in-footer
                />
            </div>
        </div>

        <RingBuilder />

        <ErrorBar />

        <ProgressIndicatorLinear
            v-qa="'app-header-progress-indicator'"
            :loading="isLazyLoadInProgress"
            class="progress-indicator-linear"
        />

        <TransitionModal>
            <Fixed
                v-if="loginOpen"
                @close="toggleLoginForm"
            >
                <ModalWithHeader
                    v-qa="'login-modal'"
                    label="Login form"
                    @close="toggleLoginForm"
                >
                    <LoginForm
                        hide-switch-to-registration
                        @login="onLogin"
                    />
                </ModalWithHeader>
            </Fixed>
        </TransitionModal>

        <TransitionModal>
            <Fixed
                v-if="savedProductsModalOpen"
                @close="toggleSavedProductsModal"
            >
                <SavedProductsModal @close="toggleSavedProductsModal" />
            </Fixed>
        </TransitionModal>
    </header>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import SavedProductsModal from '@/components/saved-products/SavedProductsModal.vue'
import {
    useElementSize,
    useEventListener,
    useWindowScroll,
    useWindowSize,
} from '@vueuse/core'

import type { HeaderMenuFragment } from '@/graphql/api'
import { useSpeakToExpertModal } from '@/composables/app/useSpeakToExpertModal'
import { useRouterNavigation } from '@/plugins/released-urls/useRouterNavigation'
import { useRoutes } from '@/router/routes'
import { useScrollPositionResolverStore } from '@/stores/app/lazyLoader'
import { getDocument } from '@/helpers/globals'
import { useIsIOS } from '@/plugins/is-ios.ts'
import { useAuth } from '@/plugins/auth.ts'
import { headerMobileBreakpoint, useIsMobile } from '@/plugins/is-mobile.ts'
import { useHideBodyOverflow } from '@/composables/useHideBodyOverflow'
import { useWishlistTrigger } from './wishlist/useWishlistTrigger'
import { useIsOnRoute } from '@/composables/useIsOnRoute'

import {
    ROUTE_PROFILE_DESIGN_STUDIO,
    ROUTE_PROFILE_MY_ORDERS,
    ROUTE_PROFILE_PRODUCTS,
} from '@/router'

import HeaderMenu from '@/components/layout/header/menu/HeaderMenu.vue'
import ThLink from '@/components/ui/buttons/ThLink.vue'
import ModalWithHeader from '@/components/layout/modal/ModalWithHeader.vue'
import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'
import Fixed from '@/components/layout/modal/Fixed.vue'
import LoginForm from '@/components/user/auth/LoginForm.vue'
import TransitionModal from '../modal/TransitionModal.vue'
import ProgressIndicatorLinear from '@/components/ui/indicators/ProgressIndicatorLinear.vue'
import RingBuilder from '@/components/rings/ring-builder/RingBuilder.vue'
import ErrorBar from '@/components/app/error-bar/ErrorBar.vue'
import WishlistTrigger from './wishlist/WishlistTrigger.vue'
import CurrencySwitcher from '@/components/app/CurrencySwitcher.vue'
import RegionSwitcher from '@/components/app/RegionSwitcher.vue'

defineProps<{
    mainMenu: HeaderMenuFragment[]
}>()

const routes = useRoutes()
const isIOS = useIsIOS()

const isMenuMobileOpen = ref(false)

useHideBodyOverflow({ isHidden: isMenuMobileOpen })

const openMenuMobile = () => {
    isMenuMobileOpen.value = true
}

const closeMenuMobile = () => {
    isMenuMobileOpen.value = false
}

const loginOpen = ref(false)

const toggleLoginForm = () => {
    loginOpen.value = !loginOpen.value
}

const { toggleSavedProductsModal, savedProductsModalOpen } =
    useWishlistTrigger()

const { isLogged } = useAuth()

const { open: openSpeakToExpertModal } = useSpeakToExpertModal()

const { t } = useI18n()
const { routerPush } = useRouterNavigation()

const onLogin = () => {
    loginOpen.value = false
    routerPush(routes.designStudio())
}

const { isLazyLoadInProgress } = storeToRefs(useScrollPositionResolverStore())

const classAppHeader = computed(() => ({
    'menu-open': isMenuMobileOpen.value,
}))

const appHeader = ref<HTMLElement | null>(null)
const { height } = useElementSize(appHeader)
const { y } = useWindowScroll()
const lastScroll = ref(0)
const headerTop = ref(0)

const isMobile = useIsMobile(headerMobileBreakpoint)
const document = getDocument()
const { height: heightBody } = useElementSize(document?.body)
const { height: heightWindow, width: widthWindow } = useWindowSize()

watch(
    [heightBody, heightWindow, widthWindow],
    ([heightBody, heightWindow, widthWindow]) => {
        if (
            heightBody <= heightWindow ||
            widthWindow >= headerMobileBreakpoint
        ) {
            headerTop.value = 0
        }
    }
)

let isScrolling = false
let isScrollingDown = true
const headerTopOnScrollEnd = ref(0)
// Time to disappear if the header is fully visible
const timeToDisappearFull = 0.34
// Calculate the time to disappear based on the amount of the header that is visible
const timeToDisappear = computed(() =>
    isIOS
        ? timeToDisappearFull
        : ((height.value - Math.abs(headerTopOnScrollEnd.value)) /
              height.value) *
          timeToDisappearFull
)
// Time to appear if the header is fully hidden
const timeToAppearFull = 0.46
// Calculate the time to appear based on the amount of the header that is visible
const timeToAppear = computed(() =>
    isIOS
        ? timeToAppearFull
        : (Math.abs(headerTopOnScrollEnd.value) / height.value) *
          timeToAppearFull
)
const transitionAppear = computed(
    () => `top ${timeToAppear.value}s var(--timing-function-easing-emphasized)`
)
const transitionDisappear = computed(
    () =>
        `top ${timeToDisappear.value}s var(--timing-function-easing-emphasized-reverse)`
)
const getHeaderTopFinal = () => {
    // Scrolling down hides the header and vice versa
    // iOS coefficients are relaxed due to lack of user control over the animation
    // if hide coefficient is 4, the header is elegible for hiding after 1/4 of its height is scrolled off the screen
    const hideCoefficient = isIOS ? 2 : 4
    // if show coefficient is 1.5, the header is elegible for showing after ~0.33% of its height is scrolled into the screen
    const showCoefficient = isIOS ? 2 : 1.5
    const thresholdCoefficient = isScrollingDown
        ? hideCoefficient
        : showCoefficient

    if (Math.abs(headerTop.value) <= height.value / thresholdCoefficient) {
        return 0
    } else {
        return -height.value
    }
}
const headerTopFinal = computed(() =>
    !isIOS ? headerTop.value : getHeaderTopFinal()
)
const isAppearing = computed(() => headerTopFinal.value === 0)
const styleAppHeader = computed(() => {
    const top = `${headerTopFinal.value}px`
    const transition =
        isScrolling && !isIOS
            ? undefined
            : isAppearing.value
              ? transitionAppear.value
              : transitionDisappear.value

    return `top: ${top}; transition: ${transition}`
})

if (document) {
    useEventListener(document, 'scroll', () => {
        isScrolling = true
    })

    useEventListener(document, 'scrollend', () => {
        isScrolling = false

        headerTopOnScrollEnd.value = headerTop.value

        headerTop.value = getHeaderTopFinal()
    })
}

watch(y, () => {
    if (!isMobile.value) {
        if (headerTop.value !== 0) {
            headerTop.value = 0
        }

        return
    }

    if (y.value > lastScroll.value) {
        isScrollingDown = true

        if (headerTop.value >= -height.value) {
            headerTop.value = Math.max(
                -height.value,
                headerTop.value - (y.value - lastScroll.value)
            )
        }

        lastScroll.value = y.value
    } else {
        isScrollingDown = false

        if (headerTop.value >= 0) {
            headerTop.value = 0
            lastScroll.value = y.value

            return
        }

        headerTop.value += lastScroll.value - y.value
        lastScroll.value = y.value
    }
})

const hideSwitchers = useIsOnRoute([
    ROUTE_PROFILE_DESIGN_STUDIO,
    ROUTE_PROFILE_MY_ORDERS,
    ROUTE_PROFILE_PRODUCTS,
])
</script>

<style lang="scss" scoped>
.saved-products {
    margin-right: 10px;
}

.app-header {
    position: sticky;
    top: 0;
    width: 100%;
    z-index: 100;
    background: $stone-2;

    .header-content {
        background: $stone-2;
        position: relative;
        width: 100%;
        height: 60px;
        max-width: 1440px;
        margin: 0 auto;
        padding: 0 10px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0;
        z-index: 10;

        .logo {
            width: 180px;
            height: 28px;
            flex-shrink: 0;
            background: url('@/assets/taylor-and-hart-logo-alt.svg') no-repeat
                center center;
            background-size: 180px auto;
            display: block;
            margin: 0 auto;
            flex-grow: 0;
            order: 10;

            @include visually-hidden-text;

            @include min-width(410) {
                margin: 0 auto;
            }

            @include min-width(1024) {
                padding: 0;
                margin: 0 0 0 10px;
                background: url('@/assets/taylor-and-hart-logo.svg') no-repeat
                    center;
                background-size: 36px auto;
                width: 44px;
                height: 44px;
                order: 1;
                flex-grow: 0;
            }

            @include min-width(1280) {
                background: url('@/assets/taylor-and-hart-logo-alt.svg')
                    no-repeat center;
                background-size: 180px auto;
                width: 180px;
                height: 28px;
                margin: 0 0 0 30px;
            }
        }

        .header-menu {
            position: absolute;
            left: 0;
            top: 0;
            width: 0;
            height: 0;
            overflow: visible;
            margin: 0;
            order: 1;

            @include min-width(1024) {
                position: static;
                width: auto;
                height: auto;
                margin: 0 auto;
                order: 2;
            }
        }

        .visit-us-button {
            display: none;
            order: 2;

            @include min-width(1024) {
                display: flex;
            }
            align-items: center;
            justify-content: center;
            margin: 0 8px;
            flex: 0 0 auto;
            width: unset;

            height: 28px;
            padding: 0 8px;

            font-family: $effra-font-stack;
            letter-spacing: 0.05em;
            font-size: 0.75rem;
            text-transform: uppercase;
            font-weight: 400;
            color: $slate;
            cursor: pointer;
            border: 1px solid $grey-500;
            border-radius: 8px;
        }

        .header-button {
            @include visually-hidden-text;

            background: transparent;
            appearance: none;
            border: none;
            width: 40px;
            height: 40px;
            display: block;
            cursor: pointer;
            text-align: center;
            line-height: 2.5rem;
            color: $slate;

            @include min-width(1024) {
                margin-left: 0;
            }

            &::before {
                font-family: $icon-font;
                display: block;
                text-indent: initial;
                font-size: 1.125rem;
            }

            &.main-menu-trigger {
                order: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 0;
                line-height: 1;

                &::before {
                    font-size: 1.125rem;
                    line-height: 1;
                    content: $icon-hamburger;
                }

                @include min-width(1024) {
                    display: none;
                }
            }

            &.contact-us-trigger {
                display: none;
                order: 3;
                margin-top: 3px;

                @include min-width(1024) {
                    display: block;
                    margin-left: 0;
                    order: 3;
                }

                &::before {
                    font-size: 1.2rem;
                    content: $icon-comment-rows;
                }
            }

            &.saved-products-trigger {
                display: none;
                order: 3;

                @include min-width(1024) {
                    display: block;
                }

                &::before {
                    color: $grey-400;
                    font-size: 1.2rem;
                    content: $icon-heart;
                }
            }

            &.cart-trigger {
                order: 30;
                display: none;
                font-size: 0;
                line-height: 1;

                &::before {
                    font-size: 1.25rem;
                    line-height: 1;
                    content: $icon-bag;
                }

                @include min-width(410) {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                @include min-width(1024) {
                    display: block;
                    font-size: 1rem;
                    line-height: 2.5rem;
                    text-align: center;
                    order: 5;

                    &::before {
                        line-height: inherit;
                    }
                }
            }
        }

        .account {
            display: none;
            order: 3;

            @include min-width(1024) {
                display: block;
            }
        }

        .visit-us-mobile {
            display: none;
            order: 1;

            @include min-width(410) {
                display: flex;
            }

            @include min-width(1024) {
                display: none;
            }
        }

        .speak-to-expert-mobile {
            display: flex;
            order: 20;

            @include min-width(1024) {
                display: none;
            }

            :deep(.icon) {
                padding-top: 4px;
            }
        }

        :deep(.button-round) {
            .icon {
                font-size: 1.15rem;
            }
        }

        .switchers {
            display: none;
            align-items: center;
            padding: 0 8px;
            height: 28px;
            // border: 1px solid $grey-500;
            border-radius: 8px;
            order: 6;

            @include min-width(1024) {
                display: flex;
                // margin: 20px 40px;
            }

            .region-switcher,
            .currency-switcher {
                background-color: transparent;
                color: $grey-500;
                :deep(.menu-button) {
                    margin: 0;

                    &::after {
                        display: none;
                    }
                }
            }

            .region-switcher {
                // margin-left: 16px;
            }
        }
    }

    .progress-indicator-linear {
        pointer-events: none;
        position: absolute;
        bottom: -5px;
        z-index: 5;
    }
}
</style>
